import React, { useState, useEffect } from 'react';
import { Search, Save, Users, Clock, ChevronDown, ChevronUp, CheckCircle, ExternalLink, ClipboardPaste, AlertTriangle, Info } from 'lucide-react';

const apiBaseUrl = process.env.REACT_APP_API_URL || '';

function gerarLinkTribunal(cnj) {
  if (!cnj) return null;
  const nums = cnj.replace(/\D/g, '');
  if (nums.length < 16) return null;
  const segmento = nums[13];
  const tribunal = parseInt(nums.substring(14, 16));

  // Justiça do Trabalho (segmento 5) - TRTs via PJe
  if (segmento === '5') {
    if (tribunal >= 1 && tribunal <= 24) {
      return {
        url: `https://pje.trt${tribunal}.jus.br/consultaprocessual/detalhe-processo/${cnj.replace(/\s/g, '')}/1`,
        nome: `TRT${tribunal} - PJe`
      };
    }
  }
  // TJSP (8.26)
  if (segmento === '8' && tribunal === 26) {
    return {
      url: `https://esaj.tjsp.jus.br/cpopg/search.do?conversationId=&cbPesquisa=NUMPROC&numeroDigitoAnoUnificado=${cnj.replace(/\s/g, '')}&foroNumeroUnificado=&dadosConsulta.valorConsultaNuUnificado=${encodeURIComponent(cnj.trim())}&dadosConsulta.valorConsulta=&dadosConsulta.tipoNuProcesso=UNIFICADO`,
      nome: 'TJSP - e-SAJ'
    };
  }
  // TRF3 (4.03)
  if (segmento === '4' && tribunal === 3) {
    return {
      url: `https://web.trf3.jus.br/base/juridica/BuscarDocumento?diretorio=Documento&txtNrProcesso=${cnj.replace(/\s/g, '')}`,
      nome: 'TRF3'
    };
  }
  // STJ (3.00)
  if (segmento === '3') {
    return {
      url: `https://processo.stj.jus.br/processo/pesquisa/?tipoPesquisa=tipoPesquisaNumeroRegistro&termo=${cnj.replace(/\s/g, '')}`,
      nome: 'STJ'
    };
  }
  // Fallback genérico CNJ
  return {
    url: `https://www.cnj.jus.br/pjecnj/ConsultaPublica/listView.seam?numeroProcesso=${encodeURIComponent(cnj.trim())}`,
    nome: 'Portal CNJ'
  };
}

export default function ConsultaProcessual({ apiBaseUrl: propApiBase }) {
  const base = propApiBase || apiBaseUrl;
  const [cnj, setCnj] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [naoEncontrado, setNaoEncontrado] = useState(false);
  const [linkTribunal, setLinkTribunal] = useState(null);
  const [erro, setErro] = useState('');
  const [expandMovimentos, setExpandMovimentos] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  // Fluxo "colar do tribunal"
  const [mostrarColar, setMostrarColar] = useState(false);
  const [textoColar, setTextoColar] = useState('');
  const [normalizando, setNormalizando] = useState(false);
  const [dadosNormalizados, setDadosNormalizados] = useState(null);
  const [erroNormalizacao, setErroNormalizacao] = useState('');

  useEffect(() => {
    fetch(`${base}/api/clientes`)
      .then(r => r.json())
      .then(data => setClientes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [base]);

  const consultar = async (e) => {
    e.preventDefault();
    if (!cnj.trim()) return;
    setLoading(true);
    setErro('');
    setResultado(null);
    setNaoEncontrado(false);
    setLinkTribunal(null);
    setSalvo(false);
    setMostrarColar(false);
    setDadosNormalizados(null);

    try {
      const res = await fetch(`${base}/api/datajud/${encodeURIComponent(cnj.trim())}`);
      const data = await res.json();
      if (res.status === 404) {
        setNaoEncontrado(true);
        setLinkTribunal(gerarLinkTribunal(cnj.trim()));
        return;
      }
      if (!res.ok) {
        setErro(data.error || 'Erro ao consultar.');
        return;
      }
      setResultado(data);
    } catch (err) {
      setErro('Erro de conexao com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  const normalizar = async () => {
    if (!textoColar.trim()) return;
    setNormalizando(true);
    setErroNormalizacao('');
    setDadosNormalizados(null);
    try {
      const res = await fetch(`${base}/api/normalizar-processo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: textoColar, cnj: cnj.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro na normalizacao.');
      setDadosNormalizados(data);
    } catch (err) {
      setErroNormalizacao(err.message || 'Erro ao processar texto.');
    } finally {
      setNormalizando(false);
    }
  };

  const salvar = async (dadosOverride) => {
    const fonte = dadosOverride || resultado;
    if (!fonte) return;
    setSalvando(true);
    const clienteNome = clienteSelecionado
      ? clientes.find(c => String(c.id) === String(clienteSelecionado))?.nome || ''
      : (dadosOverride ? (dadosOverride.parte_ativa || 'Nao informado') : (resultado.processo.partes.find(pt => pt.tipo === 'ATIVO')?.nome || 'Nao informado'));

    const payload = dadosOverride ? {
      numero: dadosOverride.numero || cnj.trim(),
      cliente: clienteNome,
      cliente_id: clienteSelecionado || null,
      tribunal: dadosOverride.tribunal || '',
      status: 'Ativo',
      descricao: dadosOverride.descricao || '',
      movimentacao: dadosOverride.ultima_movimentacao || '',
      data: dadosOverride.data_ajuizamento || '',
      numero_judicial: cnj.trim(),
      origem: 'Tribunal',
    } : {
      numero: resultado.processo.numero,
      cliente: clienteNome,
      cliente_id: clienteSelecionado || null,
      tribunal: resultado.tribunal,
      status: 'Ativo',
      descricao: `${resultado.processo.classe} — ${resultado.processo.assunto}`,
      movimentacao: resultado.processo.movimentos[0]?.nome || '',
      data: resultado.processo.dataAjuizamento?.split('T')[0] || '',
      numero_judicial: resultado.processo.numero,
      origem: 'DataJud',
    };

    try {
      await fetch(`${base}/api/processos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSalvo(true);
    } catch (err) {
      setErro('Erro ao salvar processo.');
    } finally {
      setSalvando(false);
    }
  };

  const formatarData = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      if (isNaN(d.getTime())) return str;
      return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    } catch { return str; }
  };

  const s = { // styles
    card: { background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' },
    label: { fontSize: '0.75rem', color: '#64748b', fontWeight: '600', marginBottom: '0.2rem' },
    value: { fontSize: '0.9rem', color: '#1e293b', fontWeight: '500' },
    field: { background: '#f8fafc', borderRadius: '8px', padding: '0.75rem 1rem' },
    btn: (color) => ({ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.1rem', background: color, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }),
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: '700', color: '#1e293b' }}>Consulta Processual</h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Consulta em tempo real via DataJud/CNJ</p>
      </div>

      <form onSubmit={consultar} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <input
          type="text" value={cnj} onChange={e => setCnj(e.target.value)}
          placeholder="Ex: 0000001-11.2024.8.26.0001"
          style={{ flex: 1, padding: '0.65rem 1rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none' }}
        />
        <button type="submit" disabled={loading} style={s.btn(loading ? '#93c5fd' : '#2563eb')}>
          <Search size={16} />{loading ? 'Consultando...' : 'Consultar'}
        </button>
      </form>

      {erro && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>
          {erro}
        </div>
      )}

      {/* Nao encontrado no DataJud */}
      {naoEncontrado && (
        <div style={{ ...s.card, border: '1px solid #fbbf24' }}>
          <div style={{ background: '#fffbeb', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <AlertTriangle size={20} style={{ color: '#d97706', marginTop: '0.1rem', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '0.25rem' }}>Processo nao encontrado no DataJud</div>
              <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
                O processo <strong>{cnj}</strong> ainda nao foi indexado pelo CNJ ou pertence a um tribunal com atraso de indexacao (comum em TRT2 para processos de 2026).
              </div>
            </div>
          </div>
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', borderTop: '1px solid #fde68a' }}>
            {linkTribunal && (
              <a href={linkTribunal.url} target="_blank" rel="noopener noreferrer" style={{ ...s.btn('#1d4ed8'), textDecoration: 'none' }}>
                <ExternalLink size={15} /> Abrir no {linkTribunal.nome}
              </a>
            )}
            <button onClick={() => setMostrarColar(v => !v)} style={s.btn('#0f766e')}>
              <ClipboardPaste size={15} /> Colar conteudo do tribunal
            </button>
          </div>

          {/* Colar e normalizar */}
          {mostrarColar && (
            <div style={{ padding: '0 1.5rem 1.5rem' }}>
              <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Info size={14} /> Copie o conteudo da pagina do tribunal e cole abaixo. A IA vai extrair os dados automaticamente.
              </div>
              <textarea
                value={textoColar}
                onChange={e => setTextoColar(e.target.value)}
                placeholder="Cole aqui o texto da pagina do tribunal..."
                rows={6}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace' }}
              />
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                <button onClick={normalizar} disabled={normalizando || !textoColar.trim()} style={s.btn(normalizando ? '#86efac' : '#16a34a')}>
                  {normalizando ? 'Processando...' : 'Extrair dados com IA'}
                </button>
              </div>
              {erroNormalizacao && (
                <div style={{ marginTop: '0.75rem', color: '#dc2626', fontSize: '0.875rem' }}>{erroNormalizacao}</div>
              )}

              {/* Dados normalizados */}
              {dadosNormalizados && (
                <div style={{ marginTop: '1rem', background: '#f0fdf4', borderRadius: '8px', padding: '1rem', border: '1px solid #86efac' }}>
                  <div style={{ fontWeight: '700', color: '#15803d', marginBottom: '0.75rem' }}>Dados extraidos pela IA:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    {[
                      ['Numero', dadosNormalizados.numero],
                      ['Tribunal', dadosNormalizados.tribunal],
                      ['Classe', dadosNormalizados.classe],
                      ['Data Ajuizamento', dadosNormalizados.data_ajuizamento],
                      ['Parte Ativa', dadosNormalizados.parte_ativa],
                      ['Ultima Movimentacao', dadosNormalizados.ultima_movimentacao],
                    ].map(([label, val], i) => val ? (
                      <div key={i} style={s.field}>
                        <div style={s.label}>{label}</div>
                        <div style={s.value}>{val}</div>
                      </div>
                    ) : null)}
                  </div>
                  {!salvo ? (
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', paddingTop: '0.75rem', borderTop: '1px solid #bbf7d0' }}>
                      <select value={clienteSelecionado} onChange={e => setClienteSelecionado(e.target.value)}
                        style={{ flex: 1, padding: '0.55rem 0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}>
                        <option value="">Vincular a cliente (opcional)</option>
                        {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </select>
                      <button onClick={() => salvar(dadosNormalizados)} disabled={salvando} style={s.btn(salvando ? '#86efac' : '#16a34a')}>
                        <Save size={15} />{salvando ? 'Salvando...' : 'Salvar Processo'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#16a34a', fontWeight: '600' }}>
                      <CheckCircle size={18} /> Processo salvo com sucesso!
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Resultado DataJud */}
      {resultado && (
        <div style={s.card}>
          <div style={{ background: '#1e40af', color: '#fff', padding: '1.2rem 1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8, marginBottom: '0.25rem' }}>{resultado.tribunal}</div>
                <div style={{ fontWeight: '700', fontSize: '1rem' }}>{resultado.processo.numero}</div>
              </div>
              <span style={{ background: '#3b82f6', padding: '0.3rem 0.75rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600' }}>
                {resultado.processo.grau}
              </span>
            </div>
          </div>

          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              {[
                { label: 'Classe', valor: resultado.processo.classe },
                { label: 'Orgao Julgador', valor: resultado.processo.orgaoJulgador },
                { label: 'Assunto', valor: resultado.processo.assunto },
                { label: 'Data de Ajuizamento', valor: formatarData(resultado.processo.dataAjuizamento) },
              ].map((item, i) => (
                <div key={i} style={s.field}>
                  <div style={s.label}>{item.label}</div>
                  <div style={s.value}>{item.valor || '—'}</div>
                </div>
              ))}
            </div>

            {resultado.processo.partes.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                  <Users size={16} /> Partes
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {resultado.processo.partes.map((p, i) => (
                    <div key={i} style={{ background: p.tipo === 'ATIVO' ? '#dbeafe' : '#fce7f3', color: p.tipo === 'ATIVO' ? '#1d4ed8' : '#9d174d', padding: '0.4rem 0.8rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600' }}>
                      {p.tipo}: {p.nome}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resultado.processo.movimentos.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <button onClick={() => setExpandMovimentos(!expandMovimentos)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', color: '#374151', padding: 0, marginBottom: '0.75rem' }}>
                  <Clock size={16} />
                  Movimentacoes ({resultado.processo.movimentos.length})
                  {expandMovimentos ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {expandMovimentos && (
                  <div style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: '1rem' }}>
                    {resultado.processo.movimentos.map((m, i) => (
                      <div key={i} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: i < resultado.processo.movimentos.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatarData(m.data)}</div>
                        <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: '500' }}>{m.nome}</div>
                        {m.complemento && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{m.complemento}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!salvo ? (
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '1.25rem' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Save size={16} /> Salvar no sistema
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <select value={clienteSelecionado} onChange={e => setClienteSelecionado(e.target.value)}
                    style={{ flex: 1, padding: '0.55rem 0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem' }}>
                    <option value="">Vincular a cliente (opcional)</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                  <button onClick={() => salvar(null)} disabled={salvando} style={s.btn(salvando ? '#86efac' : '#16a34a')}>
                    <Save size={15} />{salvando ? 'Salvando...' : 'Salvar Processo'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#16a34a', fontWeight: '600' }}>
                <CheckCircle size={20} /> Processo salvo com sucesso!
              </div>
            )}

            {/* Footer DataJud */}
            <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
              <Info size={13} />
              Fonte: DataJud (CNJ) &nbsp;|&nbsp; Ultima atualizacao: {formatarData(resultado.processo.movimentos[0]?.data || resultado.processo.dataAjuizamento)} &nbsp;|&nbsp;
              <AlertTriangle size={12} style={{ color: '#f59e0b' }} /> Pode haver atraso na indexacao
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
